class VariantAvailabilityNotifier {
  constructor() {
    this.VARIANT_FILTERS = ["size", "fit style", "waist", "length"];
    this.isUpdating = false;
    this.preloadedVariantData = {};
    this.productCache = new Map();
    this.gridSignature = null;
    this.cachedAvailabilityMap = null;
    this.init();
  }

  init() {
    document.addEventListener("DOMContentLoaded", () => {
      console.log("Definitive, UI-Aware Variant Notifier Initialized.");
      this.loadInitialData();
      this.setupEventListeners();
      
      window.addEventListener('load', () => {
        setTimeout(() => this.updateFilterLabels(), 200);
      });
    });
  }

  loadInitialData() {
    const dataElement = document.getElementById("product-variant-data");
    if (dataElement) {
      try {
        this.preloadedVariantData = JSON.parse(dataElement.textContent);
      } catch (e) { console.error("Failed to parse pre-loaded variant data.", e); }
    }
  }

  setupEventListeners() {
    // This master observer watches the entire page for changes.
    const debouncedUpdate = this.debounce(() => this.updateFilterLabels(), 250);
    const observer = new MutationObserver(debouncedUpdate);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('ajaxinate:load', () => this.updateFilterLabels());
  }
  
  async updateFilterLabels() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    const productHandles = [...document.querySelectorAll("#product-grid .grid__item")]
      .map(item => this.getProductHandle(item))
      .filter(Boolean)
      .sort();
      
    const newSignature = productHandles.join(',');

    // Check if the products have changed.
    if (!newSignature || !this.gridSignature || newSignature !== this.gridSignature) {
      console.log("Cache MISS: Product grid has changed. Recalculating availability map.");
      console.time("FullRecalculation");

      // --- DATA CALCULATION ---
      const productDataPromises = productHandles.map(handle => this.getProductVariants(handle));
      const allProductsVariants = await Promise.all(productDataPromises);
      
      const availabilityMap = {};
      for (const variants of allProductsVariants) {
        const productUniqueOptions = new Set();
        const productAvailableUniqueOptions = new Set();
        for (const variant of variants) {
          for (const option of variant.options) {
            const optionKey = option.toLowerCase();
            productUniqueOptions.add(optionKey);
            if (variant.available) {
              productAvailableUniqueOptions.add(optionKey);
            }
          }
        }
        productUniqueOptions.forEach(optionKey => {
          if (!availabilityMap[optionKey]) availabilityMap[optionKey] = { total: 0, available: 0 };
          availabilityMap[optionKey].total++;
        });
        productAvailableUniqueOptions.forEach(optionKey => {
          availabilityMap[optionKey].available++;
        });
      }

      this.cachedAvailabilityMap = availabilityMap;
      this.gridSignature = newSignature;
      console.timeEnd("FullRecalculation");
    } else {
      console.log("Cache HIT: Product grid is unchanged.");
    }

    
    // data is cached here
    if (this.cachedAvailabilityMap) {
      this.updateUIWithMap(this.cachedAvailabilityMap);
    }

    this.isUpdating = false;
  }
  
  updateUIWithMap(availabilityMap) {
    const filterCheckboxes = document.querySelectorAll('#main-collection-filters input[type="checkbox"]');
    
    for (const checkbox of filterCheckboxes) {
      const filterName = this.getFilterNameFromCheckbox(checkbox);
      if (!this.VARIANT_FILTERS.includes(filterName)) continue;
      
      const optionKey = checkbox.value.toLowerCase();
      const stats = availabilityMap[optionKey];
      const label = checkbox.closest('label'); 
      if (!label) continue;

      const existingNote = label.querySelector('.variant-out-of-stock-note');

      if (stats && stats.total > 0 && stats.available === 0) {
        if (!existingNote) {
          const note = document.createElement('span');
          note.className = 'variant-out-of-stock-note';
          note.textContent = '(Currently out of stock)';
          label.appendChild(note);
        }
      } else if (existingNote) {
        existingNote.remove();
      }
    }
  }

  async getProductVariants(productHandle) {
    if (this.preloadedVariantData[productHandle]) return this.preloadedVariantData[productHandle];
    if (this.productCache.has(productHandle)) return this.productCache.get(productHandle);
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      const variants = productData.variants?.map(v => ({
        available: v.available, options: [v.option1, v.option2, v.option3].filter(Boolean)
      })) || [];
      this.productCache.set(productHandle, variants);
      return variants;
    } catch (error) {
      console.warn(`Failed to fetch variants for ${productHandle}:`, error);
      return [];
    }
  }

  // Helper Functions
  getProductHandle(item) {
    const link = item.querySelector('a');
    return link ? link.href.match(/\/products\/([^\/\?]+)/)?.[1] : null;
  }
  
  getFilterNameFromCheckbox(checkbox) {
    const filterSection = checkbox.closest(".js-filter");
    const summary = filterSection?.querySelector(".facets__summary span, .mobile-facets__summary .h4");
    return summary?.textContent.trim().toLowerCase() || null;
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

new VariantAvailabilityNotifier();